import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api, fmtBRL, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { CheckCircle2, Wallet, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth, can } from "@/lib/auth";

const formatMesLabel = (ym) => {
  if (!ym || ym.length < 7) return ym;
  const [y, m] = ym.split("-");
  const months = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${months[parseInt(m, 10) - 1]}/${y}`;
};

export default function Bonuses() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [grouped, setGrouped] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [mes, setMes] = useState("");
  const [vendedorId, setVendedorId] = useState("");
  const [statusF, setStatusF] = useState("pendente");

  const load = async () => {
    const params = {};
    if (mes) params.mes = mes;
    if (statusF) params.status = statusF;
    if (vendedorId) params.vendedor_id = vendedorId;
    const [b, g, s] = await Promise.all([
      api.get("/bonuses", { params }),
      api.get("/bonuses/grouped", { params: { status: statusF || "pendente", mes } }),
      api.get("/sellers"),
    ]);
    setItems(b.data); setGrouped(g.data); setSellers(s.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mes, vendedorId, statusF]);

  const monthOptions = useMemo(() => {
    const set = new Set();
    items.forEach(b => b.mes_venda && set.add(b.mes_venda));
    grouped.forEach(g => g.mes && set.add(g.mes));
    const arr = Array.from(set).sort().reverse();
    const now = new Date();
    const cm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    if (!arr.includes(cm)) arr.unshift(cm);
    return arr;
  }, [items, grouped]);

  const pay = async (id) => {
    try { await api.post(`/bonuses/${id}/pay`); toast.success("Bonificação paga"); load(); }
    catch { toast.error("Erro"); }
  };

  const payAll = async (vendedor_id, monthFilter) => {
    const lbl = [vendedor_id ? "deste vendedor" : "todos vendedores", monthFilter ? `no mês ${formatMesLabel(monthFilter)}` : ""].filter(Boolean).join(" ");
    if (!window.confirm(`Pagar TODAS as bonificações pendentes (${lbl})?`)) return;
    try {
      const { data } = await api.post("/bonuses/pay-all", { vendedor_id, mes: monthFilter });
      toast.success(`${data.count} bonificações pagas — ${fmtBRL(data.total)}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const sn = (id) => sellers.find(s => s.id === id)?.nome || "—";
  const totalFiltrado = items.filter(b => b.status === "pendente").reduce((a, b) => a + b.valor, 0);
  const totalGrupos = grouped.reduce((a, g) => a + g.total, 0);
  const canPay = can(user, "pay_commissions");

  return (
    <Layout title="Bonificações" subtitle="≥R$10k = R$500 | ≥R$30k = R$1.000 (paga em 2 meses)">
      <Card className="p-4 rounded-sm mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs font-semibold">Mês de venda</Label>
            <SearchableSelect
              value={mes}
              onValueChange={setMes}
              options={monthOptions.map(m => ({ value: m, label: formatMesLabel(m) }))}
              placeholder="Todos os meses"
              searchPlaceholder="Buscar mês..."
              allowClear
              testid="bonus-filter-mes"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Vendedor</Label>
            <SearchableSelect
              value={vendedorId}
              onValueChange={setVendedorId}
              options={sellers.map(s => ({ value: s.id, label: s.nome }))}
              placeholder="Todos"
              searchPlaceholder="Buscar vendedor..."
              allowClear
              testid="bonus-filter-vendedor"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold">Status</Label>
            <SearchableSelect
              value={statusF}
              onValueChange={setStatusF}
              options={[
                { value: "pendente", label: "Pendente" },
                { value: "pago", label: "Pago" },
              ]}
              placeholder="Todos"
              searchPlaceholder="Buscar..."
              allowClear
              testid="bonus-filter-status"
              className="mt-1"
            />
          </div>
          <div className="flex gap-2 justify-end">
            {(mes || vendedorId || statusF !== "pendente") && (
              <Button variant="ghost" size="sm" onClick={() => { setMes(""); setVendedorId(""); setStatusF("pendente"); }} data-testid="bonus-filter-clear">
                <X className="w-4 h-4 mr-1"/>Limpar
              </Button>
            )}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Card className="p-4 rounded-sm md:col-span-2">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
            {mes ? `Total Pendente em ${formatMesLabel(mes)}` : "Total Pendente (todos os meses)"}
            {vendedorId && ` — ${sn(vendedorId)}`}
          </p>
          <p className="kpi-num text-3xl mt-2" data-testid="bonus-total">{fmtBRL(statusF === "pendente" ? totalGrupos : totalFiltrado)}</p>
          {grouped.length > 0 && statusF === "pendente" && (
            <p className="text-xs text-muted-foreground mt-1">{grouped.length} grupo(s) — {grouped.reduce((a, g) => a + g.count, 0)} bonificações</p>
          )}
        </Card>
        <Card className="p-4 rounded-sm flex flex-col justify-center gap-2">
          {canPay && grouped.length > 0 && statusF === "pendente" && (
            <Button onClick={() => payAll(vendedorId || null, mes || null)} data-testid="bonus-pay-filtered" className="w-full">
              <Wallet className="w-4 h-4 mr-1"/>
              {mes || vendedorId ? "Pagar Total Filtrado" : "Pagar Todas Pendentes"}
            </Button>
          )}
          {canPay && grouped.length === 0 && statusF === "pendente" && (
            <p className="text-xs text-muted-foreground text-center">Nenhuma bonificação pendente no filtro</p>
          )}
        </Card>
      </div>

      <Tabs defaultValue="grouped">
        <TabsList>
          <TabsTrigger value="grouped" data-testid="tab-grouped">Por Vendedor</TabsTrigger>
          <TabsTrigger value="detail" data-testid="tab-detail">Detalhado</TabsTrigger>
        </TabsList>

        <TabsContent value="grouped">
          <Card className="rounded-sm overflow-hidden">
            <Table>
              <TableHeader className="bg-secondary/60"><TableRow>
                <TableHead>Vendedor</TableHead><TableHead>Mês Venda</TableHead>
                <TableHead>Total Vendido</TableHead><TableHead>Bonificação</TableHead>
                <TableHead>Qtd</TableHead><TableHead className="text-right">Ação</TableHead>
              </TableRow></TableHeader>
              <TableBody>{grouped.map((g, idx) => (
                <TableRow key={idx} data-testid={`bonus-group-${g.vendedor_id}-${g.mes}`}>
                  <TableCell className="font-medium">{sn(g.vendedor_id)}</TableCell>
                  <TableCell>{formatMesLabel(g.mes)}</TableCell>
                  <TableCell>{fmtBRL(g.total_vendido)}</TableCell>
                  <TableCell className="font-semibold">{fmtBRL(g.total)}</TableCell>
                  <TableCell>{g.count}</TableCell>
                  <TableCell className="text-right">
                    {canPay && statusF === "pendente" && <Button data-testid={`bonus-pay-group-${g.vendedor_id}-${g.mes}`} size="sm" variant="outline" onClick={() => payAll(g.vendedor_id, g.mes)}>Pagar Tudo</Button>}
                  </TableCell>
                </TableRow>
              ))}{grouped.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Nenhum resultado para o filtro</TableCell></TableRow>}</TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="detail">
          <Card className="rounded-sm overflow-hidden">
            <Table>
              <TableHeader className="bg-secondary/60"><TableRow>
                <TableHead>Vendedor</TableHead><TableHead>Mês Venda</TableHead>
                <TableHead>Total Vendido</TableHead><TableHead>Bonificação</TableHead>
                <TableHead>Pgto Previsto</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow></TableHeader>
              <TableBody>{items.map(b => (
                <TableRow key={b.id}>
                  <TableCell>{sn(b.vendedor_id)}</TableCell>
                  <TableCell>{formatMesLabel(b.mes_venda)}</TableCell>
                  <TableCell>{fmtBRL(b.total_vendido)}</TableCell>
                  <TableCell className="font-semibold">{fmtBRL(b.valor)}</TableCell>
                  <TableCell>{fmtDate(b.pagamento_previsto)}</TableCell>
                  <TableCell><Badge variant="outline" className={b.status === "pago" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200"}>{b.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    {b.status === "pendente" && canPay && <Button size="sm" variant="ghost" onClick={() => pay(b.id)} data-testid={`bonus-pay-${b.id}`}><CheckCircle2 className="w-4 h-4"/></Button>}
                  </TableCell>
                </TableRow>
              ))}{items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">Sem bonificações</TableCell></TableRow>}</TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
